import React, { useMemo } from 'react';
import moment from 'moment';
import { Play, CheckCircle, Check, X, MagnifyingGlass } from 'phosphor-react';
import { CollectionActivityWithUser } from '@dill/dill-shared';
import { twMerge } from 'tailwind-merge';
import {
  ColFilterValue,
  DillColumn,
  DillSummaryCard,
  DillTable,
} from '../../../general/Dill/DillTable';
import { DillButton } from '../../../general/Dill';
import { Tooltip as ReactTooltip } from 'react-tooltip';

interface SupplierCollectionTasksTableProps {
  tasks: CollectionActivityWithUser[];
  onStartTask: (activity: CollectionActivityWithUser) => void;
  onCompleteTask: (activity: CollectionActivityWithUser) => void;
  onAcceptTask?: (activity: CollectionActivityWithUser) => void;
  onDeclineTask?: (activity: CollectionActivityWithUser) => void;
  onEditTask: (activity: CollectionActivityWithUser) => void;
  onViewTask?: (activity: CollectionActivityWithUser) => void;
  isLoading?: boolean;
  updatingTaskId?: string | null;
  updatingAction?: 'start' | 'complete' | 'accept' | 'decline' | null;
  height?: string;
  defaultColFilters?: Record<string, ColFilterValue>;
  currentUser?: { id: string; name: string };
  tableType?: 'recommendation' | 'normal_tasks';
  summaryCards?: DillSummaryCard[];
  /** Rendered inline with the search bar, right-aligned (e.g. "Revise AI Prompt"). */
  toolbarRight?: React.ReactNode;
}

const TASK_TYPE_STYLES: Record<string, string> = {
  CALL: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200',
  EMAIL: 'bg-purple-50 text-purple-700 ring-1 ring-purple-200',
  TEXT_MESSAGE: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  TO_DO: 'bg-gray-50 text-gray-600 ring-1 ring-gray-200',
};

const TASK_TYPE_LABELS: Record<string, string> = {
  CALL: 'Call',
  EMAIL: 'Email',
  TEXT_MESSAGE: 'Text',
  TO_DO: 'To-Do',
};

const PRIORITY_LABELS: Record<string, string> = {
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
};

const PRIORITY_STYLES: Record<string, string> = {
  HIGH: 'bg-red-50 text-red-700',
  MEDIUM: 'bg-amber-50 text-amber-700',
  LOW: 'bg-green-50 text-green-700',
};

const PRIORITY_TIER: Record<string, number> = {
  HIGH: 3,
  MEDIUM: 2,
  LOW: 1,
};

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

const STATUS_STYLES: Record<string, string> = {
  PENDING: 'bg-gray-100 text-gray-600',
  IN_PROGRESS: 'bg-blue-50 text-blue-700',
  COMPLETED: 'bg-green-50 text-green-700',
  CANCELLED: 'bg-red-50 text-red-600',
};

function SupplierCollectionTasksTable({
  tasks,
  onStartTask,
  onCompleteTask,
  onAcceptTask,
  onDeclineTask,
  onEditTask,
  onViewTask,
  isLoading,
  updatingTaskId,
  updatingAction,
  defaultColFilters,
  currentUser,
  tableType,
  summaryCards,
  toolbarRight,
}: SupplierCollectionTasksTableProps) {
  // Flatten taskDetails fields to top level so DillTable column filters can access them via row[key]
  const tableData = useMemo(
    () =>
      tasks.map((task) => ({
        ...task,
        title: task.taskDetails?.title ?? '',
        taskType: TASK_TYPE_LABELS[(task.taskDetails as any)?.taskType] ?? 'To-Do',
        buyerId: task.buyer?.name || task.buyerId,
        branch: task.buyer?.branch ?? '',
        priority: PRIORITY_LABELS[task.taskDetails?.priority ?? 'MEDIUM'] ?? 'Medium',
        status: STATUS_LABELS[task.taskDetails?.status ?? 'PENDING'] ?? 'Pending',
        assignedTo: task.assignedToUser
          ? `${task.assignedToUser.firstName} ${task.assignedToUser.lastName}`
          : '',
        assignedToUserId: task.assignedToUser?.id ?? '',
      })),
    [tasks]
  );

  const userIdToName = useMemo(() => {
    const map: Record<string, string> = {};
    if (currentUser) map[currentUser.id] = currentUser.name;
    tasks.forEach((task) => {
      if (task.assignedToUser?.id) {
        map[
          task.assignedToUser.id
        ] = `${task.assignedToUser.firstName} ${task.assignedToUser.lastName}`;
      }
    });
    return map;
  }, [tasks, currentUser]);

  const allColumns = useMemo<DillColumn<CollectionActivityWithUser>[]>(
    () => [
      {
        key: 'title',
        label: 'Task',
        width: tableType === 'normal_tasks' ? 200 : 350,
        sortable: true,
        filterable: true,
        getSortValue: (row) => row.taskDetails?.title ?? '',
        render: (_: any, row: CollectionActivityWithUser) => (
          <p className="text-xs text-left w-full truncate" title={row.taskDetails?.title || '-'}>
            {row.taskDetails?.title || '-'}
          </p>
        ),
      },
      {
        key: 'taskType',
        label: 'Type',
        width: 100,
        sortable: true,
        filterable: true,
        toggleable: true,
        getSortValue: (row) => TASK_TYPE_LABELS[(row.taskDetails as any)?.taskType] ?? 'To-Do',
        render: (_: any, row: CollectionActivityWithUser) => {
          const type = (row.taskDetails as any)?.taskType || 'TO_DO';
          return (
            <span
              className={twMerge(
                'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                TASK_TYPE_STYLES[type] || TASK_TYPE_STYLES.TO_DO
              )}>
              {TASK_TYPE_LABELS[type] || 'To-Do'}
            </span>
          );
        },
      },
      {
        key: 'buyerId',
        label: 'Customer',
        width: tableType === 'normal_tasks' ? 180 : 250,
        sortable: true,
        filterable: true,
        getSortValue: (row) => row.buyer?.name || '',
        render: (_: any, row: CollectionActivityWithUser) => (
          <p className="text-xs">{row.buyer?.name || '-'}</p>
        ),
      },
      {
        key: 'branch',
        label: 'Branch',
        width: 120,
        sortable: true,
        filterable: true,
        toggleable: true,
        getSortValue: (row) => row.buyer?.branch || '',
        render: (_: any, row: CollectionActivityWithUser) => (
          <p className="text-xs">{row.buyer?.branch || '-'}</p>
        ),
      },
      {
        key: 'dueDate',
        label: 'Due Date',
        width: 120,
        sortable: true,
        filterable: false,
        getSortValue: (row) =>
          row.taskDetails?.dueDate ? new Date(row.taskDetails.dueDate).getTime() : 0,
        render: (_: any, row: CollectionActivityWithUser) => (
          <p className="text-xs">
            {row.taskDetails?.dueDate ? moment(row.taskDetails.dueDate).format('MM/DD/YYYY') : '-'}
          </p>
        ),
      },
      {
        key: 'priority',
        label: 'Priority',
        width: 130,
        sortable: true,
        filterable: true,
        // AI-scored rows always sort as a group above unscored ones (ordered by score within
        // that group), since the score is the more granular, freshly-generated signal; unscored
        // rows (regular tasks, or older recommendations from before scoring existed) fall back
        // to priority tier among themselves.
        getSortValue: (row) => {
          const score = (row.taskDetails as any)?.aiScore;
          if (typeof score === 'number') return 1_000_000 + score;
          return PRIORITY_TIER[row.taskDetails?.priority ?? 'MEDIUM'] ?? PRIORITY_TIER.MEDIUM;
        },
        render: (_: any, row: CollectionActivityWithUser) => {
          const priority = row.taskDetails?.priority || 'MEDIUM';
          const score = (row.taskDetails as any)?.aiScore;
          return (
            <span
              className={twMerge(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                PRIORITY_STYLES[priority] || PRIORITY_STYLES.MEDIUM
              )}>
              {PRIORITY_LABELS[priority] || 'Medium'}
              {typeof score === 'number' && (
                <span className="font-normal opacity-70">· {score}/100</span>
              )}
            </span>
          );
        },
      },
      {
        key: 'status',
        label: 'Status',
        width: 120,
        sortable: true,
        filterable: true,
        toggleable: true,
        getSortValue: (row) => STATUS_LABELS[row.taskDetails?.status ?? 'PENDING'] ?? 'Pending',
        render: (_: any, row: CollectionActivityWithUser) => {
          const status = row.taskDetails?.status || 'PENDING';
          return (
            <span
              className={twMerge(
                'px-2 py-0.5 rounded-full text-xs font-medium',
                STATUS_STYLES[status] || STATUS_STYLES.PENDING
              )}>
              {STATUS_LABELS[status] || 'Pending'}
            </span>
          );
        },
      },
      {
        key: 'assignedTo',
        label: 'Assigned To',
        width: 180,
        sortable: true,
        filterable: true,
        filterKey: 'assignedToUserId',
        getFilterLabel: (userId) => userIdToName[userId] || 'Unassigned',
        getSortValue: (row) => (row as any).assignedTo || '',
        render: (_: any, row: CollectionActivityWithUser) => {
          const assigned = (row as any).assignedToUser;
          if (assigned) {
            return (
              <p
                className="text-xs break-words line-clamp-2"
                title={`${assigned.firstName} ${assigned.lastName}`}>
                {assigned.firstName} {assigned.lastName}
              </p>
            );
          }
          return <span className="text-xs text-gray-400">Unassigned</span>;
        },
      },
      {
        key: 'contacts',
        label: 'Contacts',
        width: 250,
        sortable: true,
        filterable: true,
        toggleable: true,
        getSortValue: (row) =>
          (row.references?.filter((r) => r.type === 'CONTACT') ?? [])
            .map((r) => r.description || '')
            .join(', '),
        render: (_: any, row: CollectionActivityWithUser) => {
          const contacts = row.references?.filter((r) => r.type === 'CONTACT') ?? [];
          if (contacts.length === 0) return <span className="text-xs text-gray-400">-</span>;
          const visible = contacts.slice(0, 4);
          const remaining = contacts.length - visible.length;
          return (
            <div className="flex flex-wrap gap-0.5">
              {visible.map((c) => (
                <span
                  key={c.id}
                  className="inline-flex items-center px-1.5 py-px rounded text-[10px] font-medium bg-gray-100 text-gray-600">
                  {c.description}
                </span>
              ))}
              {remaining > 0 && (
                <span
                  className="inline-flex items-center px-1.5 py-px rounded text-[10px] font-medium bg-gray-100 text-gray-500"
                  title={contacts.map((c) => c.description).join(', ')}>
                  +{remaining}
                </span>
              )}
            </div>
          );
        },
      },
      {
        key: 'actions',
        label: '',
        width: 140,
        sortable: false,
        stickyRight: tableType === 'normal_tasks' ? true : false,
        render: (_: any, row: CollectionActivityWithUser) => (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            {row.taskDetails?.isDillRecommended ? (
              <>
                <div data-tooltip-id={`accept-task-${row.id}`}>
                  <DillButton
                    variant="icon-blue"
                    icon={<Check size={14} />}
                    onClick={() => onAcceptTask?.(row)}
                    isLoading={updatingTaskId === row.id && updatingAction === 'accept'}
                    isDisabled={!!updatingTaskId}
                  />
                  <ReactTooltip
                    id={`accept-task-${row.id}`}
                    place="bottom"
                    variant="dark"
                    content="Accept recommendation"
                    className="z-40"
                  />
                </div>
                <div data-tooltip-id={`decline-task-${row.id}`}>
                  <DillButton
                    variant="icon-default"
                    icon={<X size={14} />}
                    onClick={() => onDeclineTask?.(row)}
                    isLoading={updatingTaskId === row.id && updatingAction === 'decline'}
                    isDisabled={!!updatingTaskId}
                  />
                  <ReactTooltip
                    id={`decline-task-${row.id}`}
                    place="bottom"
                    variant="dark"
                    content="Decline recommendation"
                    className="z-40"
                  />
                </div>
              </>
            ) : row.taskDetails?.taskCategory === 'TAX_EXEMPT_REVIEW' &&
              !row.taskDetails?.taxExemptDecision &&
              onViewTask ? (
              <DillButton
                variant="secondary"
                label="Review"
                icon={<MagnifyingGlass size={14} />}
                onClick={() => onViewTask(row)}
                isDisabled={!!updatingTaskId}
                height={28}
              />
            ) : row.taskDetails?.status === 'PENDING' ||
              row.taskDetails?.status === 'IN_PROGRESS' ? (
              <>
                {row.taskDetails.status === 'PENDING' ? (
                  <div data-tooltip-id={`start-task-${row.id}`}>
                    <DillButton
                      variant="icon-default"
                      icon={<Play size={14} />}
                      onClick={() => onStartTask(row)}
                      isLoading={updatingTaskId === row.id && updatingAction === 'start'}
                      isDisabled={!!updatingTaskId}
                    />
                    <ReactTooltip
                      id={`start-task-${row.id}`}
                      place="bottom"
                      variant="dark"
                      content="Start task"
                      className="z-40"
                    />
                  </div>
                ) : (
                  <div className="w-7" />
                )}
                <div data-tooltip-id={`complete-task-${row.id}`}>
                  <DillButton
                    variant="icon-blue"
                    icon={<CheckCircle size={14} />}
                    onClick={() => onCompleteTask(row)}
                    isLoading={updatingTaskId === row.id && updatingAction === 'complete'}
                    isDisabled={!!updatingTaskId}
                  />
                  <ReactTooltip
                    id={`complete-task-${row.id}`}
                    place="bottom"
                    variant="dark"
                    content="Complete task"
                    className="z-40"
                  />
                </div>
              </>
            ) : (
              <span className="text-xs text-gray-400">-</span>
            )}
          </div>
        ),
      },
    ],
    [
      onStartTask,
      onCompleteTask,
      onAcceptTask,
      onDeclineTask,
      onViewTask,
      updatingTaskId,
      updatingAction,
      userIdToName,
      tableType,
    ]
  );

  const columns = useMemo(() => {
    if (tableType === 'recommendation') {
      return allColumns.filter((col) => col.key !== 'assignedTo' && col.key !== 'contacts');
    }
    // normal_tasks: show contacts, hide nothing extra
    return allColumns;
  }, [allColumns, tableType]);

  return (
    <div className="w-full h-[calc(100vh-200px)]">
      <DillTable
        columns={columns}
        data={tableData}
        summaryCards={summaryCards}
        toolbarRight={toolbarRight}
        showActiveFilters
        sortable
        loading={isLoading}
        onRowClick={(row) => {
          if (
            onViewTask &&
            (tableType === 'recommendation' ||
              row.taskDetails?.taskCategory === 'TAX_EXEMPT_REVIEW')
          ) {
            onViewTask(row);
          } else {
            onEditTask(row);
          }
        }}
        rowHeight={40}
        emptyMessage="No tasks found."
        countLabel="Tasks"
        showCount={false}
        striped={false}
        resizable={true}
        defaultSort={
          tableType === 'recommendation'
            ? { key: 'priority', dir: 'desc' }
            : { key: 'dueDate', dir: 'desc' }
        }
        defaultColFilters={defaultColFilters}
      />
    </div>
  );
}

export default SupplierCollectionTasksTable;
